package com.fantasytenis.shared.data.tennis.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.WrappedResponse
import com.fantasytenis.shared.data.tennis.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*

/**
 * Tennis API client.
 *
 * Handles all tennis-related HTTP requests (players, matches, statistics).
 */
class TennisApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    /**
     * Get all active players.
     * GET /v2/public/tennis/players
     */
    suspend fun getPlayers(slateId: String? = null): ApiResponse<List<TennisPlayerDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}public/tennis/players")
                if (slateId != null) {
                    append("?slateId=$slateId")
                }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<TennisPlayerDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get players", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<TennisPlayerDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get players", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get player by ID.
     * GET /v2/public/tennis/players/:id
     */
    suspend fun getPlayer(playerId: String): ApiResponse<TennisPlayerDto> {
        return try {
            val response = httpClient.get("${baseUrl}public/tennis/players/$playerId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<TennisPlayerDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Player not found", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<TennisPlayerDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get player", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get live matches.
     * GET /v2/public/tennis/matches/live
     */
    suspend fun getLiveMatches(): ApiResponse<List<LiveMatchDto>> {
        return try {
            val response = httpClient.get("${baseUrl}public/tennis/matches/live")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<LiveMatchDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get live matches", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<LiveMatchDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get live matches", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get recently completed matches.
     * GET /v2/public/tennis/matches/recently-completed
     */
    suspend fun getRecentlyCompletedMatches(
        hoursAgo: Int = 24,
        limit: Int = 20
    ): ApiResponse<List<LiveMatchDto>> {
        return try {
            val url = "${baseUrl}public/tennis/matches/recently-completed?hoursAgo=$hoursAgo&limit=$limit"
            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<LiveMatchDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get completed matches", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<LiveMatchDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get completed matches", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get upcoming matches.
     * GET /v2/public/tennis/matches/upcoming
     */
    suspend fun getUpcomingMatches(tournamentId: String? = null, limit: Int? = null): ApiResponse<List<LiveMatchDto>> {
        return try {
            val url = buildString {
                append("${baseUrl}public/tennis/matches/upcoming")
                val params = mutableListOf<String>()
                if (tournamentId != null) params.add("tournamentId=$tournamentId")
                if (limit != null) params.add("limit=$limit")
                if (params.isNotEmpty()) {
                    append("?${params.joinToString("&")}")
                }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<LiveMatchDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get matches", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<LiveMatchDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get matches", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get match statistics.
     * GET /v2/public/tennis/matches/:id/statistics
     */
    suspend fun getMatchStatistics(matchId: String): ApiResponse<List<MatchStatisticsDto>> {
        return try {
            val response = httpClient.get("${baseUrl}public/tennis/matches/$matchId/statistics")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<MatchStatisticsDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get statistics", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<MatchStatisticsDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get statistics", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
